import { h, Component, Element, State, Prop, Watch, Host } from '@stencil/core';
import { ObservableMap, createStore } from '@stencil/store';

import {
  Match,
  MatchCalendarFormat,
  MatchPhase,
  MatchStatus,
  MatchUnitSize,
  MatchWording,
  LANGUAGES,
  CustomLocale,
} from '../../../services/interfaces';
import type { MatchHeaderStoreState } from '../../../services/interfaces/match-header';
import { loadDateLocale } from '../../../services/libraries';
import { parseProp, isEmpty, isNullUndefined } from '../../../services/utils';
import { isNational, setMatchWording } from '../match-unit/helpers';

import { MatchContext } from './components/match-context/match-context';
import { MatchEvents } from './components/match-events/match-events';
import { MatchLivePenalties } from './components/match-live-penalties/match-live-penalties';

const EXPORT_PARTS = [
  'agg-score',
  'away-score',
  'cta',
  'home-score',
  'match-related',
  'match-score-goals',
  'match-score',
  'post-status',
  'related',
  'status-cp',
  'status-info',
  'status-lineups',
  'status-minute',
  'status-phase',
  'status-scorers-away',
  'status-scorers-home',
  'status-scorers',
  'team-away-badge',
  'team-away-name',
  'team-away',
  'team-home-badge',
  'team-home-name',
  'team-home',
  'teams',
].join(', ');

/**
 * @part container - The component container wrapper
 * @part cta - The component CTA container included in match unit
 * @part events - The component events container
 * @part live-penalties - The component live penalties container
 *
 * @part match-events - Match events component container (scorers, red cards)
 *
 * @part events-scorers - Match events component scorers container
 * @part scorer - Match events component scorer container
 * @part scorer-name - Match events component scorer name container
 * @part scorers-away - Match events component scorers away container
 * @part scorers-home - Match events component scorers home container
 *
 * @part home-red-cards - Match cards component home red cards container
 * @part away-red-cards - Match cards component away red cards container
 *
 * @part home-missed-penalties - Match missed penalties component home penalties container
 * @part away-missed-penalties - Match missed penalties component away penalties container
 *
 * @part penalties - Match penalties component penalties container
 * @part penalties-home - Match penalties component penalties home container
 * @part penalties-away - Match penalties component penalties away container
 * @part penalties-home-wrapper - Match penalties component penalties home wrapper container
 * @part penalties-away-wrapper - Match penalties component penalties away wrapper container
 * @part penalty-home-number - Match penalties component penalty home number container
 * @part penalty-away-number - Match penalties component penalty away number container
 *
 * @part match-context - Match context component container
 * @part match-date - Match context component  match date container
 * @part round-name - Match context  component round name container
 * @part leg-name - Match context component leg name container
 * @part matchday-name - Match context component matchday name container
 */
@Component({
  tag: 'pk-match-header',
  styleUrl: './scss/pk-match-header.scss',
  shadow: true,
})
export class PkMatchHeader {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkMatchHeaderElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() _store: ObservableMap<MatchHeaderStoreState>;

  // Public Property API
  // ========================

  /** Data of the match */
  @Prop() matchData: Match;

  /** Base link of the player service used on the player scorer link.
   * Please, keep in mind the placeholder as they are, no matter if they change position in the string */
  @Prop({ reflect: true }) playerServiceUrl: string =
    'https://www.uefa.com/api/v1/linkrules/player/{{playerId}}/?competitionId={{competitionId}}';

  /** Base link of the team service url used on the home/away team link.
   * Please, keep in mind the placeholder as they are, no matter if they change position in the string */
  @Prop({ reflect: true }) teamServiceUrl: string =
    'https://www.uefa.com/api/v1/linkrules/team/{{teamId}}/?competitionId={{competitionId}}';

  /** If true, player link URL won't be available  */
  @Prop({ reflect: true }) disablePlayerLink: boolean = false;

  /** If true, home/away team link URL won't be available  */
  @Prop({ reflect: true }) disableTeamsLink: boolean = false;

  /** Compact mode, shows a minimal version of the component */
  @Prop({ reflect: true }) isCompactMode: boolean = false;

  /** CustomTimeZone dayjs timezone plugin */
  @Prop({ reflect: true }) customTimeZone: string;

  /** Calendar wording vocabulary  */
  @Prop({ reflect: true }) dateCalendarFormat: MatchCalendarFormat = {
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    nextWeek: 'ddd D MMM',
    lastDay: '[Yesterday]',
    lastWeek: 'ddd D MMM',
    sameElse: 'ddd D MMM',
  };

  /** Match wording vocabulary */
  @Prop({ reflect: true }) translations: MatchWording = {
    '1stleg': '1st leg',
    'line-ups': 'Line-ups',
    'single-leg-match': 'Single-leg fixture',
    abandoned: 'Match abandoned',
    aggregate_abbr: 'Agg',
    canceled: 'Match cancelled',
    extra_time_short: 'ET {minute}',
    full_time: 'Full time',
    ft: 'FT',
    live: 'Live',
    owngoal_abbr: 'og',
    penalty_abbr: 'p',
    postponed: 'Match postponed',
    suspended: 'Match suspended',
    futsal_foulcounter: 'Foul count',
    tbc: 'TBC',
  };

  /** Language to use (defaults to window language) */
  @Prop({ reflect: true }) language: LANGUAGES = LANGUAGES.EN;

  /** If `true`, the `displayTeamCode` will be used on the badge team's name instead */
  @Prop({ reflect: true }) useTeamCode: boolean = false;

  /** Custom dayjs locale */
  @Prop() locale: CustomLocale;

  // Prop lifecycle events
  // =======================

  @Watch('matchData')
  handleMatchChange() {
    if (!this.matchData) return;

    this._store.state.matchData = this.matchData;
  }

  @Watch('translations')
  parseTranslationsObject() {
    if (!this.translations || isEmpty(this.translations)) {
      setMatchWording(this._store.state.wording);
      return;
    }

    const wording = parseProp(this.translations);
    this._store.state.wording = wording;

    setMatchWording(this._store.state.wording);
  }

  @Watch('isCompactMode')
  @Watch('customTimeZone')
  @Watch('disablePlayerLink')
  @Watch('disableTeamsLink')
  @Watch('playerServiceUrl')
  @Watch('teamServiceUrl')
  handlePropsChange() {
    this._store.state.isCompactMode = this.isCompactMode;
    this._store.state.customTimeZone = this.customTimeZone;
    this._store.state.disablePlayerLink = this.disablePlayerLink;
    this._store.state.playerServiceUrl = this.playerServiceUrl;
    this._store.state.disableTeamsLink = this.disableTeamsLink;
    this._store.state.teamServiceUrl = this.teamServiceUrl;
  }

  @Watch('dateCalendarFormat')
  parseDateCalendarFormat() {
    this._store.state.dateCalendarFormat = this.dateCalendarFormat;
  }

  @Watch('useTeamCode')
  handleUseTeamCodeChange() {
    const useTeamCode = isNullUndefined(this.useTeamCode)
      ? isNational(null, this.matchData?.competition?.teamCategory)
      : this.useTeamCode;

    this._store.state.useTeamCode = useTeamCode;
  }

  @Watch('locale')
  @Watch('language')
  handleLanguageChange() {
    let lang = this.language.toLocaleUpperCase() as LANGUAGES;
    if (!(lang in LANGUAGES)) lang = LANGUAGES.EN;

    this._store.state.language = lang;

    if (!this.locale) {
      loadDateLocale(lang.toLowerCase());
      this._store.state.locale = lang.toLowerCase();
      return;
    }

    const locale = parseProp<ILocale>(this.locale);
    loadDateLocale(locale.name, locale);

    this._store.state.locale = locale;
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  connectedCallback() {
    this._store = createStore(INIT_STATE);

    this.handlePropsChange();
    this.handleMatchChange();
    this.handleUseTeamCodeChange();
    this.handleLanguageChange();
    this.parseDateCalendarFormat();
    this.parseTranslationsObject();
  }

  disconnectedCallback() {
    this._store.reset();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  // render() function
  // Always the last one in the class.
  // ===================================
  render() {
    if (!this._store.state.matchData) return <Host />;

    const isLivePenalties = this.matchData?.phase === MatchPhase.PENALTY && this.matchData?.status === MatchStatus.LIVE;

    return (
      <div
        class={{
          'pk-match-header flex flex-col items-center @container/match-header': true,
          'is-compact-mode': this.isCompactMode,
        }}
        part="container"
      >
        <MatchContext state={this._store.state} />
        <pk-match-unit
          exportparts={EXPORT_PARTS}
          class={{ 'pk-match-header__mu w-full py-m': true, 'is-live-penalties': isLivePenalties }}
          showLocation={!this.isCompactMode}
          size={MatchUnitSize.M}
          matchData={this.matchData}
          hideContext={this.isCompactMode}
          showFouls={true}
          hideRound={true}
          disableTeamsLink={this.disableTeamsLink}
          teamServiceUrl={this.teamServiceUrl}
          language={this.language}
          useTeamCode={this.useTeamCode}
          customTimeZone={this.customTimeZone}
          dateCalendarFormat={this.dateCalendarFormat}
          locale={this.locale}
          translations={this.translations}
        >
          <div class={{ hidden: this.isCompactMode }} slot="cta" part="cta">
            <slot />
          </div>
        </pk-match-unit>
        <div
          class={{
            hidden: this.isCompactMode || this.matchData?.phase === MatchPhase.PENALTY,
            'w-full': true,
          }}
          part="events"
        >
          <MatchEvents state={this._store.state}></MatchEvents>
        </div>
        <div class={{ 'w-full': true, hidden: this.isCompactMode }} part="live-penalties">
          <MatchLivePenalties state={this._store.state}></MatchLivePenalties>
        </div>
      </div>
    );
  }
}

const INIT_STATE: MatchHeaderStoreState = {
  isCompactMode: false,
  matchData: null,
  customTimeZone: null,
  dateCalendarFormat: {},
  disablePlayerLink: false,
  disableTeamsLink: false,
  playerServiceUrl: '',
  teamServiceUrl: '',
  wording: {},
  language: null,
  locale: null,
  useTeamCode: false,
};
